import { sha256 } from 'crypto-hash';
import { print } from 'graphql';
import { createClient } from 'graphql-ws';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { datadogLogs } from '@datadog/browser-logs';
import sortDocumentDefinitions from '@nc-aftermarket/shared/utils/sortDocumentDefinitions';
import { openAppDialog } from '../components/AppDialog';
import wsConnectionStatusVar from './wsConnectionStatusVar';
import isInvalidQueryError from './isInvalidQueryError';
import getAuthRedirectUrl from './getAuthRedirectUrl';

const retryQueries = ['MarketHome'];

const retryLink = new RetryLink({
  attempts: {
    max: 5,
    retryIf: (_error, operation) =>
      retryQueries.includes(operation.operationName),
  },
});

const queryLink = createPersistedQueryLink({
  disable: () => false,
  generateHash: query => {
    const shallowCopy = {
      ...query,
      definitions: [...query.definitions],
    };
    shallowCopy.definitions.sort(sortDocumentDefinitions);
    return sha256(print(shallowCopy));
  },
});

const httpLink = createHttpLink({
  credentials: 'include',
  uri: process.env.REACT_APP_API_ENDPOINT,
});

const wsLink = new GraphQLWsLink(
  createClient({
    keepAlive: 10_000, // ping server every 10 seconds
    on: {
      error: event => {
        wsConnectionStatusVar('disconnected');
        console.log('WebSocket connection error:', new Date(), event);
      },
      closed: event => {
        wsConnectionStatusVar('disconnected');
        console.log('WebSocket connection closed:', new Date(), event);
      },
      opened: event => {
        wsConnectionStatusVar('connected');
        console.log('WebSocket connection opened:', new Date(), event);
      },
    },
    shouldRetry: () => true,
    retryAttempts: Infinity,
    url: process.env.REACT_APP_API_ENDPOINT.replace('http', 'ws'),
    // on: {
    //   error: err => console.error('WebSocket Error:', err),
    //   closed: event => console.log('WebSocket Closed:', event),
    // },
  }),
);

const errorLink = onError(error => {
  const { graphQLErrors, networkError, operation } = error;

  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);

    // Check if the networkError is a ServerParseError
    if (networkError.name === 'ServerParseError') {
      const responseBody =
        'bodyText' in networkError ? networkError.bodyText : '';

      const statusCode =
        'statusCode' in networkError ? networkError.statusCode : null;

      datadogLogs.logger.error(
        `Failed to parse server response for operation: ${
          operation.operationName || 'Unnamed'
        }`,
        {
          serverParseError: {
            statusCode,
            bodyText: responseBody,
            message: networkError.message,
          },
          operationName: operation.operationName,
          query: operation.query ? operation.query.loc.source.body : 'N/A',
          variables: operation.variables,
        },
      );
    }
  }

  if (graphQLErrors?.[0]?.message.includes('ERR_REAUTH_REQUIRED')) {
    window.location.href = getAuthRedirectUrl({
      returnTo: window.location.pathname,
    });
  }

  if (isInvalidQueryError(error)) {
    openAppDialog('invalidQuery');
  }
});

const paginatedFieldMerge = (existing = { items: [] }, incoming, options) => {
  const { page = 1, pageSize = 0 } = options.args || {};

  const offset = (page - 1) * pageSize;
  const newItems = [...(existing.items ?? [])];
  const incomingItems = incoming?.items ?? [];

  for (let i = 0; i < incomingItems.length; ++i) {
    newItems[offset + i] = incoming.items[i];
  }

  return {
    ...incoming,
    items: newItems,
  };
};

const paginatedFieldKeyArgs = ['filter', 'sort'];

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  from([retryLink, errorLink, queryLink, httpLink]),
);

export const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Collection: {
        fields: {
          sales: {
            keyArgs: paginatedFieldKeyArgs,
            merge: paginatedFieldMerge,
          },
        },
      },
      Product: {
        keyFields: ['name'],
      },
      Query: {
        fields: {
          binSales: {
            keyArgs: paginatedFieldKeyArgs,
            merge: paginatedFieldMerge,
          },
          sales: {
            keyArgs: paginatedFieldKeyArgs,
            merge: paginatedFieldMerge,
          },
        },
      },
      User: {
        fields: {
          bidding: {
            keyArgs: paginatedFieldKeyArgs,
            merge: paginatedFieldMerge,
          },
          invoices: {
            keyArgs: paginatedFieldKeyArgs,
            merge: paginatedFieldMerge,
          },
          favorites: {
            keyArgs: paginatedFieldKeyArgs,
            merge: paginatedFieldMerge,
          },
        },
      },
      UserDisplayName: {
        keyFields: ['value'],
      },
    },
  }),
  connectToDevTools: true,
  link: splitLink,
});

export const csvClient = new ApolloClient({
  cache: new InMemoryCache({
    // This completely disables normalization
    dataIdFromObject: () => null,
    // This prevents any type policies from being applied
    typePolicies: {},
  }),
  defaultOptions: {
    // Disable cache for queries
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
    // Disable cache for mutations
    mutate: {
      errorPolicy: 'all',
    },
  },
  link: from([retryLink, errorLink, queryLink, httpLink]),
});
